import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  Pressable,
  Dimensions,
  StyleSheet,
  ScrollView,
  Linking,
  Platform,
} from "react-native";
import { StackScreenProps } from "@react-navigation/stack";
import { ClientCard, PlatformDropdown, Button } from "../components";
import { COLORS, PLATFORM_WIDTH } from "../constants/ui";
import type { RootStackParamList } from "../components/util";
import { rekomendowana, sieci } from "../data/placowki";
import { miasta, najblizszeMiasto } from "../data/miasta";

type TestDetailsScreenProps = StackScreenProps<RootStackParamList, "TestDetails">;
let { height } = Dimensions.get("window");
const KLUCZ_MIASTA = "testreminderMiasto";

export const TestDetails: React.FC<TestDetailsScreenProps> = ({ navigation }) => {
  const [open, setOpen] = useState(false);
  const [miasto, setMiasto] = useState<string>("Łódź");
  const [items, setItems] = useState(
    miasta.map((m) => ({ label: m.nazwa, value: m.nazwa }))
  );
  const [stan, setStan] = useState<"brak" | "pytam" | "ok" | "odmowa">("brak");

  useEffect(() => {
    try {
      const z = (globalThis as any)?.localStorage?.getItem(KLUCZ_MIASTA);
      if (z) setMiasto(z);
    } catch (e) {}
  }, []);

  const zapiszMiasto = (m: string) => {
    setMiasto(m);
    try {
      (globalThis as any)?.localStorage?.setItem(KLUCZ_MIASTA, m);
    } catch (e) {}
  };

  // automatyczne wskazanie miasta po udostepnieniu lokalizacji przez przegladarke
  const wykryjLokalizacje = () => {
    const geo = (globalThis as any)?.navigator?.geolocation;
    if (!geo) return setStan("odmowa");
    setStan("pytam");
    geo.getCurrentPosition(
      (poz: any) => {
        const { latitude, longitude } = poz.coords;
        zapiszMiasto(najblizszeMiasto(latitude, longitude).nazwa);
        setStan("ok");
      },
      () => setStan("odmowa"),
      { timeout: 10000, maximumAge: 600000 }
    );
  };

  const komunikat =
    stan === "pytam"
      ? "Pytam przeglądarkę o lokalizację..."
      : stan === "ok"
      ? "Ustawione z Twojej lokalizacji. Możesz zmienić."
      : stan === "odmowa"
      ? "Nie udało się pobrać lokalizacji. Wybierz miasto z listy."
      : "Wybierz miasto albo pozwól mi ustalić je automatycznie.";

  return (
    <View style={styles.container}>
      <View style={styles.wrapper}>
        <Text style={styles.naglowek}>Twoja lokalizacja</Text>
        <Text style={styles.podpis}>{komunikat}</Text>

        <PlatformDropdown
          open={open}
          setOpen={setOpen}
          value={miasto}
          setValue={(cb: any) =>
            zapiszMiasto(typeof cb === "function" ? cb(miasto) : cb)
          }
          items={items}
          setItems={setItems}
        />

        {Platform.OS === "web" && (
          <View style={{ marginTop: 10 }}>
            <Button color={COLORS.TEAL} textColor={COLORS.BLACK} onPress={wykryjLokalizacje}>
              Użyj mojej lokalizacji
            </Button>
          </View>
        )}

        <Pressable style={open && styles.overlayOpen} onPress={() => setOpen(false)}></Pressable>

        <ScrollView style={styles.locationDisplay} showsVerticalScrollIndicator={false}>
          <Text style={styles.sekcja}>Placówka rekomendowana</Text>
          <ClientCard
            {...rekomendowana}
            onPress={() => navigation.navigate("LabDetails", { labName: rekomendowana.name })}
          />
          {rekomendowana.adress.city !== miasto && (
            <Text style={styles.uwaga}>
              Salve Medica jest w Łodzi, a wybrano {miasto}. Sprawdź też sieci poniżej.
            </Text>
          )}

          <Text style={styles.sekcja}>Sieci ogólnopolskie</Text>
          {sieci.map((s) => (
            <View key={s.nazwa} style={styles.karta}>
              <Text style={styles.kartaNazwa}>{s.nazwa}</Text>
              <Text style={styles.kartaOpis}>{s.opis}</Text>
              <Pressable onPress={() => Linking.openURL(s.wyszukiwarka)}>
                <Text style={styles.link}>Znajdź punkt pobrań: {miasto}</Text>
              </Pressable>
            </View>
          ))}

          <Text style={styles.stopka}>
            Punkty pojedynczych sieci prowadzą do ich własnych wyszukiwarek, żeby adresy
            i godziny były aktualne. Cen nie pokazuję, bo nie mam cenników.
          </Text>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, height: height, alignItems: "center", backgroundColor: COLORS.LIGHTBLUE },
  wrapper: {
    justifyContent: "flex-start",
    width: PLATFORM_WIDTH,
    height: "100%",
    paddingHorizontal: 20,
    paddingTop: 16,
    backgroundColor: COLORS.DARKBLUE,
    borderTopRightRadius: 30,
  },
  naglowek: { color: "#fff", fontSize: 16, fontWeight: "bold", marginBottom: 4 },
  podpis: { color: "#D6D2F2", fontSize: 12, marginBottom: 10 },
  sekcja: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "bold",
    marginTop: 18,
    marginBottom: 8,
    letterSpacing: 1,
  },
  uwaga: { color: "#D6D2F2", fontSize: 12, marginTop: 8 },
  karta: { backgroundColor: "#fff", borderRadius: 14, padding: 14, marginBottom: 10 },
  kartaNazwa: { fontSize: 16, fontWeight: "bold", marginBottom: 4 },
  kartaOpis: { fontSize: 12.5, color: "#444", marginBottom: 8 },
  link: { fontSize: 13, fontWeight: "bold", color: COLORS.DARKBLUE },
  stopka: { color: "#B8B2E8", fontSize: 11, marginTop: 14, marginBottom: 30, lineHeight: 16 },
  locationDisplay: { borderColor: COLORS.DARKBLUE, width: "100%", marginTop: 8 },
  overlayOpen: {
    position: "absolute",
    top: 0,
    left: 0,
    width: PLATFORM_WIDTH,
    height: "100%",
    backgroundColor: COLORS.DARKBLUE,
    zIndex: 29,
    opacity: 0.5,
    borderTopRightRadius: 30,
  },
});
